import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getProfessionalSportsQuestions, 
  getProfessionalSportsConfig, 
  submitProfessionalSportsAssessment,
  getProfessionalSportsResults,
  getProfessionalSportsDashboard,
  getOrganization
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  ClipboardCheck, 
  ChevronRight, 
  ChevronLeft,
  CheckCircle,
  Shield,
  AlertTriangle,
  Building2,
  AlertCircle,
  Loader2,
  Award,
  Zap,
  Server,
  Lock,
  FileText,
  Upload,
  Check,
  X,
  Info,
  Target,
  TrendingUp,
  BarChart3,
  AlertOctagon
} from 'lucide-react';

// Scoring labels for 0-4 scale
const SCORE_LABELS = {
  0: { label: 'No formal control', description: 'No documented process or control in place', color: 'bg-red-500' },
  1: { label: 'Ad-hoc', description: 'Informal processes, inconsistently applied', color: 'bg-orange-500' },
  2: { label: 'Developing', description: 'Documented but not fully implemented or monitored', color: 'bg-yellow-500' },
  3: { label: 'Established', description: 'Documented, implemented, and regularly monitored', color: 'bg-blue-500' },
  4: { label: 'Best-in-class', description: 'Optimized, continuously improved, industry-leading', color: 'bg-green-500' },
};

// Pillar icons
const PILLAR_ICONS = {
  'facilities_infrastructure': Building2,
  'event_operations': Zap,
  'cyber_data': Lock,
};

// Pillar colors
const PILLAR_COLORS = {
  'facilities_infrastructure': 'text-blue-500 bg-blue-500/10 border-blue-500/30',
  'event_operations': 'text-orange-500 bg-orange-500/10 border-orange-500/30',
  'cyber_data': 'text-purple-500 bg-purple-500/10 border-purple-500/30',
};

// Risk band colors
const RISK_BAND_COLORS = {
  'Elite': 'bg-emerald-500 text-white',
  'Strong': 'bg-green-500 text-white',
  'Moderate': 'bg-yellow-500 text-black',
  'Elevated': 'bg-orange-500 text-white',
  'Critical': 'bg-red-500 text-white',
};

export default function ProfessionalSportsAssessment() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State
  const [organization, setOrganization] = useState(null);
  const [config, setConfig] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Assessment state
  const [currentPillarIndex, setCurrentPillarIndex] = useState(0);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  
  // Results state
  const [assessmentResult, setAssessmentResult] = useState(null);
  const [showResults, setShowResults] = useState(false);
  
  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Load organization
        const orgResponse = await getOrganization(orgId);
        setOrganization(orgResponse.data);
        
        // Check org type
        if (orgResponse.data.org_type !== 'professional_sports') {
          setError('This assessment is only available for Professional Sports organizations.');
          setLoading(false);
          return;
        }
        
        // Load config
        const configResponse = await getProfessionalSportsConfig();
        setConfig(configResponse.data);
        
        // Load questions
        const questionsResponse = await getProfessionalSportsQuestions();
        setQuestions(questionsResponse.data.questions || []);
        
      } catch (err) {
        console.error('Error loading assessment data:', err);
        setError(err.response?.data?.detail || 'Failed to load assessment data');
      } finally {
        setLoading(false);
      }
    };
    
    if (orgId) {
      loadData();
    }
  }, [orgId]);
  
  // Get pillars and sections
  const pillars = config?.pillar_config ? Object.keys(config.pillar_config) : [];
  const currentPillar = pillars[currentPillarIndex];
  const currentPillarConfig = currentPillar ? config?.pillar_config[currentPillar] : null;
  
  // sections is an object, get keys as array
  const sectionsObj = currentPillarConfig?.sections || {};
  const sections = Object.keys(sectionsObj);
  const currentSectionKey = sections[currentSectionIndex];
  const currentSectionConfig = sectionsObj[currentSectionKey];
  
  // Get questions for current section
  const currentQuestions = questions.filter(
    q => q.pillar === currentPillar && q.section === currentSectionKey
  );
  
  // Calculate progress
  const totalQuestions = questions.length;
  const answeredQuestions = Object.keys(answers).length;
  const progressPercent = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;
  
  // Get section progress
  const getSectionProgress = (pillar, sectionKey) => {
    const sectionQuestions = questions.filter(q => q.pillar === pillar && q.section === sectionKey);
    const sectionAnswered = sectionQuestions.filter(q => answers[q.id] !== undefined).length;
    return { answered: sectionAnswered, total: sectionQuestions.length };
  };
  
  // Handle answer change
  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        answer: parseInt(value),
      }
    }));
  };
  
  // Handle notes change
  const handleNotesChange = (questionId, notes) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        notes,
      }
    }));
  };
  
  // Navigation
  const goToNextSection = () => {
    if (currentSectionIndex < sections.length - 1) {
      setCurrentSectionIndex(prev => prev + 1);
    } else if (currentPillarIndex < pillars.length - 1) {
      setCurrentPillarIndex(prev => prev + 1);
      setCurrentSectionIndex(0);
    }
    window.scrollTo(0, 0);
  };
  
  const goToPrevSection = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(prev => prev - 1);
    } else if (currentPillarIndex > 0) {
      setCurrentPillarIndex(prev => prev - 1);
      const prevPillar = pillars[currentPillarIndex - 1];
      const prevSectionsObj = config?.pillar_config[prevPillar]?.sections || {};
      setCurrentSectionIndex(Object.keys(prevSectionsObj).length - 1);
    }
    window.scrollTo(0, 0);
  };
  
  const isFirstSection = currentPillarIndex === 0 && currentSectionIndex === 0;
  const isLastSection = currentPillarIndex === pillars.length - 1 && currentSectionIndex === sections.length - 1;
  
  // Check if all questions are answered
  const allQuestionsAnswered = questions.every(q => answers[q.id]?.answer !== undefined);
  
  // Submit assessment
  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      // Format answers for API
      const formattedAnswers = Object.entries(answers).map(([questionId, data]) => ({
        question_id: questionId,
        answer: data.answer,
        notes: data.notes || null,
      }));
      
      const response = await submitProfessionalSportsAssessment(orgId, formattedAnswers);
      setAssessmentResult(response.data);
      setShowConfirmSubmit(false);
      setShowResults(true);
      toast.success('Assessment submitted successfully!');
      
    } catch (err) {
      console.error('Error submitting assessment:', err);
      toast.error(err.response?.data?.detail || 'Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Render loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading assessment...</p>
        </div>
      </div>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <div className="max-w-2xl mx-auto mt-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button onClick={() => navigate(-1)} className="mt-4">
          <ChevronLeft className="w-4 h-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }
  
  // Render results
  if (showResults && assessmentResult) {
    return (
      <AssessmentResults 
        result={assessmentResult} 
        organization={organization}
        onClose={() => navigate(`/organizations/${orgId}`)}
      />
    );
  }
  
  const PillarIcon = currentPillar ? PILLAR_ICONS[currentPillar] || Shield : Shield;
  
  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Award className="w-6 h-6 text-purple-500" />
            Professional Sports Assessment
          </h1>
          <p className="text-muted-foreground mt-1">
            {organization?.name} • Operations & Infrastructure Risk Assessment
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-sm">
            {answeredQuestions} / {totalQuestions} Questions
          </Badge>
        </div>
      </div>
      
      {/* Progress Overview */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Overall Progress</span>
            <span className="text-sm text-muted-foreground">{Math.round(progressPercent)}%</span>
          </div>
          <Progress value={progressPercent} className="h-2" />
          
          {/* Pillar Progress */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            {pillars.map((pillar, index) => {
              const pillarConfig = config?.pillar_config[pillar];
              const pillarQuestions = questions.filter(q => q.pillar === pillar);
              const pillarAnswered = pillarQuestions.filter(q => answers[q.id]?.answer !== undefined).length;
              const pillarProgress = pillarQuestions.length > 0 ? (pillarAnswered / pillarQuestions.length) * 100 : 0;
              const Icon = PILLAR_ICONS[pillar] || Shield;
              const isActive = index === currentPillarIndex;
              
              return (
                <button
                  key={pillar}
                  onClick={() => {
                    setCurrentPillarIndex(index);
                    setCurrentSectionIndex(0);
                  }}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    isActive 
                      ? `${PILLAR_COLORS[pillar]} border-2` 
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={`w-4 h-4 ${isActive ? '' : 'text-muted-foreground'}`} />
                    <span className={`text-xs font-medium ${isActive ? '' : 'text-muted-foreground'}`}>
                      {pillarConfig?.name || pillar}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Progress value={pillarProgress} className="h-1 flex-1" />
                    <span className="text-xs text-muted-foreground">{pillarAnswered}/{pillarQuestions.length}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>
      
      {/* Current Section */}
      <Card className="bg-card border-border">
        <CardHeader className={`border-b ${PILLAR_COLORS[currentPillar]}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <PillarIcon className="w-6 h-6" />
              <div>
                <CardTitle className="text-lg">{currentPillarConfig?.name}</CardTitle>
                <CardDescription className="text-sm mt-0.5">
                  {typeof currentSectionConfig === 'string' ? currentSectionConfig : (currentSectionConfig?.name || currentSectionKey)} • Weight: {(currentPillarConfig?.weight * 100).toFixed(0)}%
                </CardDescription>
              </div>
            </div>
            <Badge variant="secondary">
              Section {currentSectionIndex + 1} of {sections.length}
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent className="p-6">
          {/* Section Navigation */}
          <div className="flex flex-wrap gap-2 mb-6 pb-4 border-b border-border">
            {sections.map((sectionKey, idx) => {
              const sectionConf = sectionsObj[sectionKey];
              const progress = getSectionProgress(currentPillar, sectionKey);
              const isComplete = progress.answered === progress.total;
              const isCurrent = idx === currentSectionIndex;
              
              return (
                <button
                  key={sectionKey}
                  onClick={() => setCurrentSectionIndex(idx)}
                  data-testid={`section-tab-${sectionKey}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${
                    isCurrent 
                      ? 'bg-primary text-primary-foreground' 
                      : isComplete 
                        ? 'bg-green-500/20 text-green-600 hover:bg-green-500/30' 
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {isComplete && <Check className="w-3 h-3" />}
                  {typeof sectionConf === 'string' ? sectionConf : (sectionConf?.name || sectionKey)}
                  <span className="opacity-60">({progress.answered}/{progress.total})</span>
                </button>
              );
            })}
          </div>
          
          {/* Questions */}
          <div className="space-y-6">
            {currentQuestions.map((question, qIndex) => {
              const answer = answers[question.id];
              const isCritical = question.critical_flag;
              
              return (
                <div 
                  key={question.id} 
                  className={`p-4 rounded-lg border ${
                    isCritical ? 'border-red-500/50 bg-red-500/5' : 'border-border bg-muted/30'
                  }`}
                  data-testid={`question-${question.id}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 text-primary text-sm font-medium shrink-0">
                      {qIndex + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2 mb-2">
                        <p className="font-medium text-sm leading-relaxed">{question.question}</p>
                        {isCritical && (
                          <Badge variant="destructive" className="shrink-0 text-xs">
                            <AlertOctagon className="w-3 h-3 mr-1" />
                            Critical
                          </Badge>
                        )}
                      </div>
                      
                      {question.description && (
                        <p className="text-xs text-muted-foreground mb-4">{question.description}</p>
                      )}
                      
                      {/* Score Selection */}
                      <div className="space-y-3">
                        <Label className="text-xs font-medium">Score (0-4)</Label>
                        <div className="grid grid-cols-5 gap-2">
                          {[0, 1, 2, 3, 4].map(score => {
                            const scoreInfo = SCORE_LABELS[score];
                            const isSelected = answer?.answer === score;
                            
                            return (
                              <button
                                key={score}
                                onClick={() => handleAnswerChange(question.id, score)}
                                className={`p-2 rounded-lg border-2 transition-all text-center ${
                                  isSelected 
                                    ? `${scoreInfo.color} text-white border-transparent` 
                                    : 'border-border hover:border-primary/50 bg-background'
                                }`}
                                data-testid={`score-${question.id}-${score}`}
                              >
                                <div className="text-lg font-bold">{score}</div>
                                <div className={`text-[10px] leading-tight ${isSelected ? 'text-white/90' : 'text-muted-foreground'}`}>
                                  {scoreInfo.label}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        
                        {/* Selected score description */}
                        {answer?.answer !== undefined && (
                          <div className="flex items-center gap-2 p-2 rounded bg-muted/50">
                            <Info className="w-4 h-4 text-muted-foreground shrink-0" />
                            <span className="text-xs text-muted-foreground">
                              {SCORE_LABELS[answer.answer].description}
                            </span>
                          </div>
                        )}
                        
                        {/* Notes */}
                        <div className="mt-3">
                          <Label className="text-xs font-medium mb-1.5 block">Notes (Optional)</Label>
                          <Textarea
                            placeholder="Add any relevant notes or documentation references..."
                            value={answer?.notes || ''}
                            onChange={(e) => handleNotesChange(question.id, e.target.value)}
                            className="min-h-[60px] text-sm"
                            data-testid={`notes-${question.id}`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
            <Button
              variant="outline"
              onClick={goToPrevSection}
              disabled={isFirstSection}
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>
            
            <div className="text-sm text-muted-foreground">
              Pillar {currentPillarIndex + 1} of {pillars.length}
            </div>
            
            {isLastSection ? (
              <Button
                onClick={() => setShowConfirmSubmit(true)}
                disabled={!allQuestionsAnswered}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Submit Assessment
              </Button>
            ) : (
              <Button onClick={goToNextSection}>
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Legend */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Scoring Guide</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(SCORE_LABELS).map(([score, info]) => (
              <div key={score} className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded ${info.color} flex items-center justify-center text-white text-xs font-bold`}>
                  {score}
                </div>
                <div>
                  <div className="text-xs font-medium">{info.label}</div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      
      {/* Confirm Submit Dialog */}
      <Dialog open={showConfirmSubmit} onOpenChange={setShowConfirmSubmit}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit Assessment?</DialogTitle>
            <DialogDescription>
              You have answered all {totalQuestions} questions. Once submitted, 
              the assessment cannot be modified. Your Enterprise Operations Risk Score 
              will be calculated based on your responses.
            </DialogDescription>
          </DialogHeader>
          
          {/* Quick summary */}
          <div className="space-y-3 py-4">
            <div className="text-sm font-medium">Assessment Summary:</div>
            {pillars.map(pillar => {
              const pillarConfig = config?.pillar_config[pillar];
              const pillarQuestions = questions.filter(q => q.pillar === pillar);
              const pillarAnswered = pillarQuestions.filter(q => answers[q.id]?.answer !== undefined).length;
              const avgScore = pillarQuestions.reduce((sum, q) => sum + (answers[q.id]?.answer || 0), 0) / pillarQuestions.length;
              
              return (
                <div key={pillar} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{pillarConfig?.name}</span>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">{pillarAnswered}/{pillarQuestions.length}</Badge>
                    <span className="text-xs text-muted-foreground">
                      Avg: {avgScore.toFixed(1)}/4
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmSubmit(false)}>
              Review Answers
            </Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-green-600 hover:bg-green-700">
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirm Submit
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Assessment Results Component
function AssessmentResults({ result, organization, onClose }) {
  const riskBandColor = RISK_BAND_COLORS[result.risk_band?.band] || 'bg-gray-500';
  
  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className={`inline-flex items-center gap-2 px-6 py-3 rounded-full ${riskBandColor} mb-4`}>
          <Award className="w-6 h-6" />
          <span className="text-xl font-bold">{result.risk_band?.band}</span>
        </div>
        <h1 className="text-3xl font-bold">{organization?.name}</h1>
        <p className="text-muted-foreground mt-2">Professional Sports Operations & Infrastructure Assessment</p>
      </div>
      
      {/* Enterprise Score */}
      <Card className="bg-card border-border">
        <CardContent className="p-6 text-center">
          <div className="text-6xl font-bold text-primary mb-2">
            {result.enterprise_score?.toFixed(1)}
          </div>
          <div className="text-lg font-medium text-muted-foreground">
            Enterprise Operations Risk Score
          </div>
          <div className="text-sm text-muted-foreground mt-1">
            {result.risk_band?.description}
          </div>
        </CardContent>
      </Card>
      
      {/* Critical Flags */}
      {result.has_critical_flags && (
        <Alert variant="destructive">
          <AlertOctagon className="h-5 w-5" />
          <AlertDescription className="ml-2">
            <strong>CRITICAL OPERATIONAL FLAGS DETECTED</strong>
            <ul className="mt-2 space-y-1">
              {result.critical_flags?.map((flag, idx) => (
                <li key={idx} className="text-sm">
                  • {flag.name}: {flag.description}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}
      
      {/* Pillar Scores */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Pillar Scores
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Object.entries(result.pillar_scores || {}).map(([pillar, data]) => {
              const Icon = PILLAR_ICONS[pillar] || Shield;
              const percentage = data.percentage || 0;
              
              return (
                <div key={pillar} className={`p-4 rounded-lg border ${PILLAR_COLORS[pillar]}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{data.name}</span>
                  </div>
                  <div className="text-3xl font-bold mb-1">{percentage.toFixed(1)}%</div>
                  <Progress value={percentage} className="h-2 mb-2" />
                  <div className="text-xs text-muted-foreground">
                    Weight: {(data.weight * 100).toFixed(0)}% • {data.points_earned}/{data.max_points} points
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      
      {/* Top 5 Lowest Scoring Controls */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-orange-500" />
            Priority Improvement Areas
          </CardTitle>
          <CardDescription>Top 5 lowest scoring controls requiring attention</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {result.lowest_scoring_controls?.slice(0, 5).map((control, idx) => (
              <div 
                key={idx} 
                className="flex items-start gap-3 p-3 rounded-lg bg-muted/50"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                  control.score === 0 ? 'bg-red-500' : control.score === 1 ? 'bg-orange-500' : 'bg-yellow-500'
                }`}>
                  {control.score}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{control.question}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {control.pillar} • {control.section}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      
      {/* Heat Map */}
      {result.heat_map && result.heat_map.length > 0 && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Section Heat Map
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(
                result.heat_map.reduce((acc, item) => {
                  if (!acc[item.pillar]) acc[item.pillar] = [];
                  acc[item.pillar].push(item);
                  return acc;
                }, {})
              ).map(([pillar, sections]) => (
                <div key={pillar}>
                  <div className="text-sm font-medium mb-2">{pillar}</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {sections.map((section, idx) => {
                      const score = section.score;
                      let bgColor = 'bg-red-500';
                      if (score >= 90) bgColor = 'bg-emerald-500';
                      else if (score >= 75) bgColor = 'bg-green-500';
                      else if (score >= 60) bgColor = 'bg-yellow-500';
                      else if (score >= 40) bgColor = 'bg-orange-500';
                      
                      return (
                        <div 
                          key={idx}
                          className={`p-3 rounded-lg ${bgColor} text-white`}
                        >
                          <div className="text-xs opacity-80">{section.section}</div>
                          <div className="text-lg font-bold">{score.toFixed(0)}%</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Insurance Optimization */}
      {result.insurance_optimization && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-500" />
              Insurance Optimization
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className={`px-4 py-2 rounded-lg ${
                result.insurance_optimization.premium_credit_eligible 
                  ? 'bg-green-500/20 text-green-600' 
                  : 'bg-red-500/20 text-red-600'
              }`}>
                {result.insurance_optimization.premium_credit_eligible ? (
                  <span className="flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    Eligible for Premium Credit
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <X className="w-4 h-4" />
                    Not Eligible for Premium Credit
                  </span>
                )}
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-3">
              {result.insurance_optimization.recommendation}
            </p>
          </CardContent>
        </Card>
      )}
      
      {/* Actions */}
      <div className="flex justify-center gap-4">
        <Button variant="outline" onClick={onClose}>
          <ChevronLeft className="w-4 h-4 mr-2" />
          Return to Organization
        </Button>
        <Button onClick={() => window.print()}>
          <FileText className="w-4 h-4 mr-2" />
          Print Report
        </Button>
      </div>
    </div>
  );
}
